import { forwardRef, memo } from 'react';
import type { ComponentPropsWithoutRef } from 'react';

import { cx } from '@/utils/cx';

import { DEFAULT_MAX_BAR_WIDTH_PX, DEFAULT_TOUCH_TARGET_PX, computeMenuToggleVars } from './geometry';
import { MenuToggleBars } from './MenuToggleBars';
import styles from './MenuToggleButton.module.scss';

export interface MenuToggleIconProps extends Omit<ComponentPropsWithoutRef<'span'>, 'children'> {
  /** 현재 토글 상태. 시각만 표현 — 호스트가 상태를 관리해야 함. */
  toggled?: boolean;
  /** 버튼(정사각형) 한 변의 길이(px). 12~96으로 clamp. */
  touchTargetPx?: number;
  /** 한 줄 막대의 가로 길이 상한(px). 기본 32. */
  maxBarWidthPx?: number;
}

/**
 * 햄버거 ↔ X 시각 표현만 담당하는 비-interactive 컴포넌트.
 * `<button>` 자식으로 안전하게 들어갈 수 있음 (Header.Action.icon 슬롯 등).
 * 인터랙션·상태 관리가 필요하면 `MenuToggleButton` 사용.
 */
export const MenuToggleIcon = memo(
  forwardRef<HTMLSpanElement, MenuToggleIconProps>(function MenuToggleIcon(
    {
      toggled = false,
      touchTargetPx = DEFAULT_TOUCH_TARGET_PX,
      maxBarWidthPx = DEFAULT_MAX_BAR_WIDTH_PX,
      className,
      style,
      ...rest
    },
    ref,
  ) {
    const cssVars = computeMenuToggleVars(touchTargetPx, maxBarWidthPx);

    return (
      <span
        aria-hidden="true"
        {...rest}
        className={cx(styles.iconContainer, className)}
        ref={ref}
        style={{ ...style, ...cssVars }}
      >
        <MenuToggleBars toggled={toggled} />
      </span>
    );
  }),
);

MenuToggleIcon.displayName = 'MenuToggleIcon';
